/*
 * The breakpoint vocabulary in code: what `useViewport()` asks the browser, and what the keyboard
 * hides. The media strings are the ones `tokens.css` defines its `compact`, `touch` and
 * `standalone` variants with, and the test beside this file reads them back out of the stylesheet,
 * so CSS and code always agree on what "a phone" means.
 */

/**
 * The media query behind each variant: `compact` is below 1024px, the phone's layout; `touch` a
 * coarse pointer, at any width; `standalone` the installed app, without the browser's chrome.
 */
export const viewportQueries = {
  compact: "(width < 64rem)",
  touch: "(pointer: coarse)",
  standalone: "(display-mode: standalone)",
} as const

/** What the viewport is: each variant, true where it matches. */
export type Viewport = Record<keyof typeof viewportQueries, boolean>

/**
 * How a link to a file opens: in the installed app, in a window of its own, since a file opened in
 * place takes over the app's one window and leaves it no way back; in a browser, as any link does.
 */
export const fileLinkOf = (standalone: boolean) =>
  standalone ? ({ target: "_blank", rel: "noreferrer" } as const) : {}

/** The classes that make a subtree `compact` or `touch` in CSS, as `.dark` makes one dark. */
export const forcedClass = { compact: "compact", touch: "touch" } as const

/** The visual viewport, as far as the keyboard goes: `window.visualViewport` fits it. */
export type VisualViewport = { height: number; offsetTop: number }

/**
 * How many pixels of the page's bottom the on-screen keyboard covers: the layout viewport's height
 * less the bottom of the visual viewport within it. iOS shrinks only the visual viewport for its
 * keyboard (and may scroll it, which `offsetTop` carries), so a bar docked at the bottom has to be
 * lifted by this much. Zero with no keyboard, and on Android, which resizes the page itself.
 */
export function keyboardInsetOf(innerHeight: number, { height, offsetTop }: VisualViewport) {
  return Math.max(0, Math.round(innerHeight - height - offsetTop))
}

/**
 * How far short of its tallest at the same width the page has to stand before the on-screen
 * keyboard counts as up: more than a browser's own bars take as they come and go, and less than any
 * phone's keyboard.
 */
export const keyboardShortfall = 150

/**
 * Whether the on-screen keyboard is up, by the visual viewport's `height` against the `tallest` it
 * has stood at the same width. iOS shrinks the visual viewport for the keyboard, and Android
 * (`interactive-widget=resizes-content`) the whole page, so each shows as the page standing well
 * short of its full height. A hardware keyboard's shortcut bar on an iPad doesn't.
 */
export function keyboardShownOf(tallest: number, height: number) {
  return tallest - height > keyboardShortfall
}

/** What an element is, as far as the on-screen keyboard goes: `document.activeElement` fits it. */
export type Focused = {
  tagName: string
  isContentEditable?: boolean
  type?: string
  readOnly?: boolean
}

/** The input types a phone answers with a picker, a toggle or nothing, rather than its keyboard. */
const keyless = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
])

/**
 * Whether focus on `element` brings up the on-screen keyboard: a text field, a writable textarea or
 * an editable region (the markdown editor). A button, a checkbox or a Select doesn't.
 */
export function typesText(element: Focused | null) {
  if (!element) return false
  if (element.isContentEditable) return true
  const tag = element.tagName.toLowerCase()
  if (tag === "textarea") return !element.readOnly
  return tag === "input" && !element.readOnly && !keyless.has(element.type ?? "text")
}
